import React, { useMemo } from 'react';
import moment from 'moment';
import { ThemeProvider, Box, Tooltip, IconButton } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { maskDecimal } from '../../../../../../utils/masks';

export default function FinanceiroTabPanel({
    profile,
    theme,
    data,
    manual,
    mrt,
    podeEditar,
    podeCancelar,
    onEditarRow,
    onCancelarRow,
}) {
    const columns = useMemo(
        () => [
            {
                id: 'financeiro',
                header: `Situação no financeiro para o ${profile.labels.labParceiro}`,
                columns: [
                    {
                        accessorKey: 'valorOriginal',
                        header: 'Valor Total',
                        muiTableBodyCellProps: ({ row }) => ({
                            align: 'right',
                            onClick: () => {
                                console.info(row.original);
                            },
                        }),
                        accessorFn: (row) =>
                            row.valorOriginal == null
                                ? ''
                                : 'R$ ' +
                                  parseFloat(row.valorOriginal).toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                  }),
                    },
                    { accessorKey: 'parcelas', header: 'Parcelas', muiTableBodyCellProps: { align: 'center' } },
                    {
                        accessorKey: 'dataVencimento',
                        header: 'Vencimento',
                        filterVariant: 'date',
                        sortingFn: 'date',
                        Cell: ({ row }) => (
                            <span>
                                {row.original.dataVencimento ? moment.utc(row.original.dataVencimento).format('DD/MM/YYYY') : ''}
                            </span>
                        ),
                        muiTableBodyCellProps: { align: 'center' },
                    },
                    {
                        accessorKey: 'valor',
                        header: 'Valor',
                        muiTableBodyCellProps: { align: 'right' },
                        accessorFn: (row) =>
                            row.valor == null
                                ? ''
                                : 'R$ ' +
                                  parseFloat(row.valor).toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                  }),
                    },
                    {
                        accessorKey: 'valorBaixado',
                        header: 'Baixado',
                        muiTableBodyCellProps: { align: 'right' },
                        accessorFn: (row) =>
                            row.valorBaixado == null
                                ? ''
                                : 'R$ ' +
                                  parseFloat(row.valorBaixado).toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                  }),
                    },
                    {
                        accessorKey: 'valorAberto',
                        header: 'Aberto',
                        muiTableBodyCellProps: { align: 'right' },
                        accessorFn: (row) =>
                            row.valorAberto == null
                                ? ''
                                : 'R$ ' +
                                  parseFloat(row.valorAberto).toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                  }),
                    },
                    {
                        accessorKey: 'dataLancamento',
                        header: 'Data',
                        filterVariant: 'date',
                        sortingFn: 'date',
                        Cell: ({ row }) => (
                            <span>
                                {row.original.dataLancamento ? moment.utc(row.original.dataLancamento).format('DD/MM/YYYY') : ''}
                            </span>
                        ),
                        muiTableBodyCellProps: { align: 'center' },
                    },
                    { accessorKey: 'Empresa.nomeFantasia', id: 'Empresa.nomeFantasia', header: 'Empresa' },
                    { accessorKey: 'Conta.descricao', id: 'Conta.descricao', header: 'Conta' },
                    { accessorKey: 'pagamentoForma.descricao', id: 'pagamentoForma.descricao', header: 'Forma' },
                    { accessorKey: 'pagamentoPrazo.descricao', id: 'pagamentoPrazo.descricao', header: 'Prazo' },
                ],
            },
        ],
        [profile]
    );

    return (
        <div style={{ margin: '1em' }}>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    columns={columns}
                    data={data || []}
                    rowCount={mrt?.rowCount ?? data?.length ?? 0}
                    getRowId={(row) => row.uuid}
                    manualPagination={!!manual}
                    manualSorting={!!manual}
                    manualFiltering={!!manual}
                    enableColumnFilterModes
                    enableGlobalFilterModes
                    enableColumnOrdering
                    enableEditing
                    renderRowActions={({ row }) => (
                        <Box sx={{ display: 'flex', gap: '1rem' }}>
                            <Tooltip arrow placement="left" title="Editar">
                                <span>
                                    <IconButton onClick={() => onEditarRow?.(row.original)} disabled={!podeEditar}>
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip arrow placement="right" title="Cancelar">
                                <span>
                                    <IconButton
                                        color="error"
                                        onClick={() => onCancelarRow?.(row.original)}
                                        disabled={!podeCancelar}
                                    >
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <div style={{ paddingLeft: 8 }}>
                            <strong>Total Aberto:</strong> {data?.[0]?.totalAberto ? maskDecimal(data?.[0]?.totalAberto) : '0,00'}
                        </div>
                    )}
                    onColumnFiltersChange={mrt?.setColumnFilters}
                    onColumnFilterFnsChange={mrt?.setColumnFilterFnsMode}
                    onGlobalFilterChange={mrt?.setGlobalFilter}
                    onPaginationChange={mrt?.setPagination}
                    onSortingChange={mrt?.setSorting}
                    state={{
                        columnFilterFns: mrt?.columnFilterFns,
                        columnFilters: mrt?.columnFilters,
                        globalFilter: mrt?.globalFilter,
                        isLoading: mrt?.isLoading,
                        pagination: mrt?.pagination,
                        showAlertBanner: mrt?.isError,
                        showProgressBars: mrt?.isRefetching,
                        sorting: mrt?.sorting,
                    }}
                    muiToolbarAlertBannerProps={mrt?.isError ? { color: 'error', children: 'Error loading data' } : undefined}
                    positionExpandColumn="first"
                    positionActionsColumn="first"
                    positionToolbarAlertBanner="bottom"
                    localization={MRT_Localization_PT_BR}
                    initialState={{ showColumnFilters: true, density: 'compact' }}
                />
            </ThemeProvider>
        </div>
    );
}
